// Documents générés (sprint 5, contrat C0 v1.3 proposé) : lecture tolérante de la
// spécification `kind: "document"` émise par le backend, formats, libellés, échéance.
// Module pur (aucun import React) : testé par node --test, partagé par la carte,
// le skeleton et la visionneuse du panneau.
//
// Spécification attendue (artifact.spec) — champs plats ; les variantes imbriquées
// `file {url, name, size_bytes}` / `preview {url, pages}` sont aussi acceptées :
// {
//   version: "1.3", kind: "document",
//   format: "pdf" | "docx" | "xlsx" | "pptx",
//   title, subtitle?, template?, template_label?,
//   filename, download_url: "/download/<dossier>/<uuid>/<nom>",
//   preview_url?: "/preview/…/<nom>.pdf"    // rendu PDF du même modèle (tout format)
//   slides_preview_url?: "/preview/…/<nom>_diapositives.pdf"  // présentation : PDF des
//                                            // diapositives converti par LibreOffice (16:9)
//   slides_preview_status?: "ok" | "indisponible" | "echec"   // présentation seulement
//   size_bytes?, pages?, slides?, sheets?: ["Synthèse", …],
//   generated_at?, generation_ms?, expires_at?,   // 24 h par défaut
//   source?: {tools: [], labels: [], periode?}, sections?: [],
//   origin?: "assistant" | "report"               // rapport composé sans IA
//          | "agent"                              // rapport d'expertise d'un agent (P1),
//                                                 // gardé 7 jours (AGENTS_RAPPORTS_DIR)
// }

export const DOCUMENT_TTL_HOURS = 24;
// Rapport d'un agent : durée de vie par défaut (AGENTS_RAPPORTS_JOURS du serveur, contrat P1
// §8) quand la spécification ne donne pas expires_at ; l'échéance se dit alors en jours
export const RAPPORT_AGENT_JOURS = 7;
const HOUR = 60 * 60 * 1000;
const JOUR = 24 * HOUR;

import { DOCUMENT_FORMATS } from './documentFormats.js';

export { DOCUMENT_FORMATS, documentFromResponse, FORMAT_ORDER } from './documentFormats.js';

// Libellés des modèles (DOC-07 à DOC-11, DOC-17) quand le backend n'en donne pas
export const TEMPLATE_LABELS = {
  rapport_mensuel: 'Rapport mensuel de flotte',
  analyse_carburant: 'Analyse carburant',
  bilan_maintenance: 'Bilan maintenance',
  sinistralite: 'Sinistralité',
  tco: 'Coût total de possession',
  composite: 'Rapport composé',
  synthese_direction: 'Synthèse direction',
  libre: 'Document',
};

const ALIASES = { doc: 'docx', word: 'docx', xls: 'xlsx', excel: 'xlsx', ppt: 'pptx', powerpoint: 'pptx' };

/** Format normalisé (`pdf`, `docx`, `xlsx`, `pptx`) ou null. */
export function normalizeFormat(value) {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase().replace(/^\./, '');
  const format = ALIASES[key] ?? key;
  return DOCUMENT_FORMATS[format] ? format : null;
}

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const num = (v) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null);

/**
 * Lien de fichier généré sûr : chemin relatif /download/… (pièce jointe) ou /preview/…
 * (aperçu PDF affichable) du même domaine (nginx en production, proxy Vite en dev).
 * Un lien absolu n'est accepté que vers la même origine (jamais un autre site : pas
 * d'aperçu ni de « Copier le lien » vers l'extérieur).
 */
const FILE_PREFIX_RE = /^\/(download|preview)\/[^/]/;

export function safeDownloadPath(value, origin = null) {
  const raw = str(value);
  if (!raw) return null;
  if (FILE_PREFIX_RE.test(raw) && !raw.includes('..')) return raw.split('#')[0];
  if (origin) {
    try {
      const url = new URL(raw);
      if (url.origin === origin && FILE_PREFIX_RE.test(url.pathname) && !url.pathname.includes('..')) {
        return url.pathname + url.search;
      }
    } catch { /* lien invalide */ }
  }
  return null;
}

const nameFromUrl = (url) => {
  const raw = url?.split(/[?#]/)[0].split('/').pop();
  if (!raw) return null;
  try { return decodeURIComponent(raw); } catch { return raw; }
};

function parseDate(value) {
  if (value == null) return null;
  const t = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

/**
 * Spécification émise → modèle d'affichage. null si le document n'est pas exploitable
 * (format inconnu ou lien de téléchargement absent / externe).
 */
export function normalizeDocumentSpec(raw, { origin = null } = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const file = raw.file && typeof raw.file === 'object' ? raw.file : {};
  const preview = raw.preview && typeof raw.preview === 'object' ? raw.preview : {};

  const downloadUrl = safeDownloadPath(raw.download_url ?? raw.url ?? file.url ?? file.download_url, origin);
  const filename = str(raw.filename) ?? str(file.name) ?? nameFromUrl(downloadUrl);
  const format = normalizeFormat(raw.format ?? file.format) ?? normalizeFormat(filename?.split('.').pop());
  if (!format || !downloadUrl) return null;

  const previewUrl = safeDownloadPath(raw.preview_url ?? preview.url ?? preview.pdf_url, origin);
  // Présentation : PDF des diapositives (LibreOffice) ; sans lien exploitable, l'état dit
  // pourquoi (LibreOffice absent du serveur, conversion en échec) ; null : document antérieur
  const slidesPreviewUrl = format === 'pptx' ? safeDownloadPath(raw.slides_preview_url, origin) : null;
  const slidesStatus = str(raw.slides_preview_status);
  const slidesPreview = format !== 'pptx' ? null
    : slidesPreviewUrl ? 'ok'
      : slidesStatus === 'indisponible' ? 'indisponible'
        : slidesStatus === 'echec' || slidesStatus === 'ok' ? 'echec' : null;
  const generatedAt = parseDate(raw.generated_at ?? raw.created_at);
  const explicitExpiry = parseDate(raw.expires_at);
  const origine = raw.origin === 'report' || raw.origin === 'agent' ? raw.origin : 'assistant';
  const duree = origine === 'agent' ? RAPPORT_AGENT_JOURS * JOUR : DOCUMENT_TTL_HOURS * HOUR;
  const expiresAt = explicitExpiry ?? (generatedAt != null ? generatedAt + duree : null);
  const template = str(raw.template);
  const sheets = Array.isArray(raw.sheets)
    ? raw.sheets.map((s) => (typeof s === 'string' ? s : str(s?.name))).filter(Boolean)
    : [];

  return {
    format,
    info: DOCUMENT_FORMATS[format],
    title: str(raw.title) ?? filename,
    subtitle: str(raw.subtitle),
    template,
    templateLabel: str(raw.template_label) ?? (template ? TEMPLATE_LABELS[template] ?? null : null),
    filename,
    downloadUrl,
    previewUrl,
    slidesPreviewUrl,
    slidesPreview,
    sizeBytes: num(raw.size_bytes ?? raw.size ?? file.size_bytes ?? file.size),
    pages: num(raw.pages ?? raw.page_count ?? preview.pages),
    previewPages: num(raw.preview_pages ?? preview.pages),
    slides: num(raw.slides ?? raw.slide_count),
    sheets,
    generatedAt,
    generationMs: num(raw.generation_ms ?? raw.duree_ms),
    expiresAt,
    periode: str(raw.periode ?? raw.source?.periode),
    sources: Array.isArray(raw.source?.labels) ? raw.source.labels.filter((l) => typeof l === 'string') : [],
    origin: origine,
  };
}

const nf1 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat('fr-FR');
const UNITS = ['o', 'Ko', 'Mo', 'Go'];

export function formatSize(bytes) {
  if (bytes == null) return null;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? nf0.format(value) : nf1.format(value)} ${UNITS[unit]}`;
}

const plural = (n, word) => `${nf0.format(n)} ${word}${n > 1 ? 's' : ''}`;

/** « 4 pages », « 6 diapositives », « 3 feuilles » ; null si inconnu. */
export function formatExtent(doc) {
  if (!doc) return null;
  if (doc.format === 'pptx') return doc.slides != null ? plural(doc.slides, 'diapositive') : (doc.pages != null ? plural(doc.pages, 'diapositive') : null);
  if (doc.format === 'xlsx') return doc.sheets.length ? plural(doc.sheets.length, 'feuille') : null;
  return doc.pages != null ? plural(doc.pages, 'page') : null;
}

/** Durée de génération : « 1,4 s », « 820 ms ». */
export function formatDuration(ms) {
  if (ms == null) return null;
  return ms < 1000 ? `${nf0.format(Math.round(ms))} ms` : `${nf1.format(ms / 1000)} s`;
}

/**
 * Échéance du lien (fichiers conservés 24 h ; rapports d'agent, 7 jours) :
 * {expired, text} — « Disponible encore 23 h », « Expire dans 12 min », « Lien expiré » ;
 * rapport d'agent à plus d'un jour de son échéance : « Disponible encore 6 jours ».
 */
export function expiryState(doc, now = Date.now()) {
  const agent = doc?.origin === 'agent';
  if (!doc?.expiresAt) {
    return { expired: false, text: agent ? `Lien valable ${RAPPORT_AGENT_JOURS} jours après la génération` : `Lien valable ${DOCUMENT_TTL_HOURS} h après la génération` };
  }
  const left = doc.expiresAt - now;
  if (left <= 0) return { expired: true, text: agent ? `Lien expiré : rapports d’agent conservés ${RAPPORT_AGENT_JOURS} jours` : 'Lien expiré : fichiers conservés 24 h' };
  if (left < HOUR) return { expired: false, text: `Expire dans ${Math.max(1, Math.round(left / 60000))} min` };
  if (agent && left >= JOUR) {
    const jours = Math.floor(left / JOUR);
    return { expired: false, text: `Disponible encore ${jours} jour${jours > 1 ? 's' : ''}` };
  }
  return { expired: false, text: `Disponible encore ${Math.floor(left / HOUR)} h` };
}

/** Ligne de métadonnées de la carte : « Rapport mensuel · 4 pages · 182 Ko · généré en 1,4 s ». */
export function metaParts(doc) {
  if (!doc) return [];
  return [
    formatExtent(doc),
    formatSize(doc.sizeBytes),
    doc.generationMs != null ? `généré en ${formatDuration(doc.generationMs)}` : null,
  ].filter(Boolean);
}

/**
 * Onglets de la visionneuse selon le format : présentation convertie par LibreOffice,
 * « Diapositives » d'abord (le fichier tel qu'il sera projeté) ; puis l'aperçu PDF ; puis
 * la vue native (Word, Excel).
 */
export function viewerTabs(doc) {
  if (!doc) return [];
  const tabs = [];
  if (doc.slidesPreviewUrl) tabs.push({ key: 'slides', label: 'Diapositives' });
  if (doc.previewUrl || doc.format === 'pdf') tabs.push({ key: 'pdf', label: 'Aperçu PDF' });
  if (doc.format === 'docx') tabs.push({ key: 'docx', label: 'Mise en page Word' });
  if (doc.format === 'xlsx') tabs.push({ key: 'xlsx', label: 'Classeur' });
  return tabs;
}

/**
 * Note de l'aperçu PDF d'une présentation : ce rendu du même modèle est au format rapport
 * (A4), pas une image de chaque diapositive. La note évite de confondre « 5 pages »
 * d'aperçu et « 12 diapositives » du fichier, et dit où voir les diapositives en 16:9 :
 * onglet « Diapositives », ou téléchargement quand le serveur n'a pas pu les convertir
 * (LibreOffice absent, conversion en échec). null sinon.
 */
export function previewNote(doc) {
  if (!doc || doc.format !== 'pptx' || !doc.previewUrl) return null;
  const pages = doc.previewPages != null ? ` (${plural(doc.previewPages, 'page')})` : '';
  const one = doc.slides === 1;
  const slides = one ? 'la diapositive' : doc.slides != null ? `les ${plural(doc.slides, 'diapositive')}` : 'les diapositives';
  const base = `Aperçu au format rapport A4${pages} : mêmes contenus et mêmes chiffres que ${slides}`;
  if (doc.slidesPreviewUrl) return `${base}, ${one ? 'présentée' : 'présentées une par une'} en 16:9 dans l’onglet « Diapositives ».`;
  if (doc.slidesPreview === 'indisponible') {
    return `${base}. Aperçu diapositive par diapositive indisponible sur ce serveur (convertisseur LibreOffice non installé) : téléchargez la présentation pour la voir en 16:9.`;
  }
  if (doc.slidesPreview === 'echec') {
    return `${base}. L’aperçu diapositive par diapositive n’a pas pu être produit pour ce document : téléchargez la présentation pour la voir en 16:9.`;
  }
  return `${base}. Téléchargez la présentation pour la voir en 16:9.`;
}

// Diapositives (une page 16:9 par diapositive) : chaque page ajustée au cadre de la
// visionneuse PDF du navigateur (paramètre d'ouverture ignoré là où il n'est pas reconnu)
export const SLIDES_VIEW_FRAGMENT = '#view=Fit';

/** Note de l'onglet « Diapositives » : nombre, format, et fidélité du rendu du serveur. */
export function slidesNote(doc) {
  if (!doc?.slidesPreviewUrl) return null;
  const count = doc.slides === 1 ? 'La diapositive de la présentation, en 16:9' : `${doc.slides != null ? `Les ${plural(doc.slides, 'diapositive')}` : 'Les diapositives'} de la présentation, en 16:9, une par page`;
  return `${count}. Rendu du serveur : polices et graphiques peuvent différer légèrement dans PowerPoint.`;
}

/** URL de l'aperçu PDF : celle du rendu fourni, sinon le fichier lui-même s'il est un PDF. */
export function pdfPreviewUrl(doc) {
  if (!doc) return null;
  return doc.previewUrl ?? (doc.format === 'pdf' ? doc.downloadUrl : null);
}

